import { type ChangeEvent, type FormEvent } from 'react'
import { snapshot } from 'valtio'
import { addToArray } from '../../utils/arrayUtils'
import { virtualTerminalProxy } from '../../../routes/app/virtual-terminal/index/virtualTerminalProxy'
import { executeCustomerCardsSearch } from '../../apiUtils/executeCardsSearch'
import { executeCustomerAchSearch } from '../../apiUtils/executeAchSearch'
import { executeFetchSessionTokenForCreditCard } from '../../apiUtils/executeFetchSessionToken'
import {
  VirtualTerminalProxyFormFieldNamesEnum
} from '../../types/virtualTerminalProxyFormFieldNamesEnum'
import { recurringPaymentNewProxy } from '../../../routes/app/recurring-charges/new/recurringPaymentNewProxy'
import {
  RecurringPaymentNewProxyFormFieldNamesEnum
} from '../../../routes/app/recurring-charges/new/recurringPaymentNewProxyFormFieldNamesEnum'
import {
  recurringPaymentEditProxy
} from '../../../routes/app/recurring-charges/[id]/modals/EditSlideOver/recurringPaymentEditProxy'
import {
  rpOccurrenceEditProxy
} from '../../../routes/app/recurring-charges/[id]/rp-occurrence/[id]/RpOccurrenceDetail/CardUpdateSlideover/rpOccurrenceEditProxy'

import { GtagEvents, gtmTrackButtonPress } from '../../utils/gtmUtils'
import { resetVirtualTerminalCardAddProxy, cardAddProxy } from './cardAddProxy'
import { apiExecuteCustomerCardCreate } from './apiExecuteCustomerCardCreate'
import { type CardAddFormFieldNamesEnum } from './cardAddFormFieldNamesEnum'
import { normalizers } from './normalizers'
import { validators } from './validators'
import { executeConvergeOpenLightbox } from './executeConvergeOpenLightbox'

export const closeModal = (): void => {
  cardAddProxy.displaySlideOverCAP = false
  gtmTrackButtonPress(GtagEvents.CARD_MODAL_CANCEL)

  resetVirtualTerminalCardAddProxy()
}

export const handleChange = (fieldName: CardAddFormFieldNamesEnum) => (e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => {
  // @ts-expect-error
  const val: string = e.target.type === 'checkbox' ? e.target.checked : e.target.value
  const normalizer = normalizers[fieldName]
  const normalized = normalizer ? normalizer(val) : val
  cardAddProxy.formValues[fieldName] = normalized
  cardAddProxy.formDirty = addToArray(cardAddProxy.formDirty, fieldName)
  const validator = validators[fieldName]
  if (validator) { cardAddProxy.formErrors[fieldName] = validator(normalized) }
}

export const handleBlur = (fieldName: CardAddFormFieldNamesEnum) => () => {
  cardAddProxy.formBlurred = addToArray(cardAddProxy.formBlurred, fieldName)
}

export const handleSubmitToOpenLightboxRetryRpOccurrence = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
  e.preventDefault()

  cardAddProxy.displayConvergeSlideOver = true

  await executeFetchSessionTokenForCreditCard()
  const { apiCallSessionToken, converge } = snapshot(cardAddProxy)
  if (apiCallSessionToken.error || !converge.sessionToken) { return }

  setTimeout(() => {
    cardAddProxy.displayConvergeSlideOver = false
  }, 60000 * 14)

  await executeConvergeOpenLightbox().then(() => {
    //this should only happen after a successful return of the thing...BC
    void executeCreateCardAfterLightboxRetryRpOccurrence()
  })
}

export async function executeCreateCardAfterLightboxRetryRpOccurrence (): Promise<void> {
  gtmTrackButtonPress(GtagEvents.CARD_MODAL_SUBMIT)
  const { customer } = snapshot(rpOccurrenceEditProxy)
  const customerCard = await apiExecuteCustomerCardCreate({ customerId: String(customer?.id) })
  const { apiCallCustomerCardCreate } = snapshot(cardAddProxy)
  if (apiCallCustomerCardCreate.error || !customerCard) { return }

  await executeCustomerCardsSearch()
  await executeCustomerAchSearch()

  rpOccurrenceEditProxy.customerCard = customerCard
  rpOccurrenceEditProxy.form.dirty = addToArray(rpOccurrenceEditProxy.form.dirty, RecurringPaymentNewProxyFormFieldNamesEnum.CustomerCard)
  resetVirtualTerminalCardAddProxy()
}

export const handleSubmitToOpenLightboxEditRecurringConfiguration = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
  e.preventDefault()

  cardAddProxy.displayConvergeSlideOver = true

  await executeFetchSessionTokenForCreditCard()
  const { apiCallSessionToken, converge } = snapshot(cardAddProxy)
  if (apiCallSessionToken.error || !converge.sessionToken) { return }

  setTimeout(() => {
    cardAddProxy.displayConvergeSlideOver = false
  }, 60000 * 14)

  await executeConvergeOpenLightbox().then(() => {
    //this should only happen after a successful return of the thing...BC
    void executeCreateCardAfterLightboxEditRecurring()
  })
}

export async function executeCreateCardAfterLightboxEditRecurring (): Promise<void> {
  gtmTrackButtonPress(GtagEvents.CARD_MODAL_SUBMIT)
  const { customer } = snapshot(recurringPaymentEditProxy)
  const customerCard = await apiExecuteCustomerCardCreate({ customerId: String(customer?.id) })
  const { apiCallCustomerCardCreate } = snapshot(cardAddProxy)
  if (apiCallCustomerCardCreate.error || !customerCard) { return }

  await executeCustomerCardsSearch()
  await executeCustomerAchSearch()

  recurringPaymentEditProxy.customerCard = customerCard
  recurringPaymentEditProxy.form.dirty = addToArray(recurringPaymentEditProxy.form.dirty, VirtualTerminalProxyFormFieldNamesEnum.CustomerCard)
  resetVirtualTerminalCardAddProxy()
}
export const handleSubmitToOpenLightboxVirtualTerminal = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
  e.preventDefault()

  cardAddProxy.displayConvergeSlideOver = true

  await executeFetchSessionTokenForCreditCard()
  const { apiCallSessionToken, converge } = snapshot(cardAddProxy)
  if (apiCallSessionToken.error || !converge.sessionToken) { return }

  setTimeout(() => {
    cardAddProxy.displayConvergeSlideOver = false
  }, 60000 * 14)

  await executeConvergeOpenLightbox().then(() => {
    //this should only happen after a successful return of the thing...BC
    void executeCreateCardAfterLightboxVirtualTerminal()
  })
}

export async function executeCreateCardAfterLightboxVirtualTerminal (): Promise<void> {
  gtmTrackButtonPress(GtagEvents.CARD_MODAL_SUBMIT)
  const { customer } = snapshot(virtualTerminalProxy)
  const customerCard = await apiExecuteCustomerCardCreate({ customerId: String(customer?.id) })
  const { apiCallCustomerCardCreate } = snapshot(cardAddProxy)
  if (apiCallCustomerCardCreate.error || !customerCard) { return }

  await executeCustomerCardsSearch()
  await executeCustomerAchSearch()

  virtualTerminalProxy.customerCard = customerCard
  virtualTerminalProxy.form.dirty = addToArray(virtualTerminalProxy.form.dirty, VirtualTerminalProxyFormFieldNamesEnum.CustomerCard)
  resetVirtualTerminalCardAddProxy()
}

export const handleSubmitToOpenLightboxNewRecurring = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
  e.preventDefault()

  cardAddProxy.displayConvergeSlideOver = true

  await executeFetchSessionTokenForCreditCard()
  const { apiCallSessionToken, converge } = snapshot(cardAddProxy)
  if (apiCallSessionToken.error || !converge.sessionToken) { return }

  setTimeout(() => {
    cardAddProxy.displayConvergeSlideOver = false
  }, 60000 * 14)

  await executeConvergeOpenLightbox().then(() => {
    //this should only happen after a successful return of the thing...BC
    void executeCreateCardAfterLightboxNewRecurring()
  })
}

export async function executeCreateCardAfterLightboxNewRecurring (): Promise<void> {
  gtmTrackButtonPress(GtagEvents.CARD_MODAL_SUBMIT)
  const { customer } = snapshot(recurringPaymentNewProxy)
  const customerCard = await apiExecuteCustomerCardCreate({ customerId: String(customer?.id) })
  const { apiCallCustomerCardCreate } = snapshot(cardAddProxy)
  if (apiCallCustomerCardCreate.error || !customerCard) { return }

  await executeCustomerCardsSearch()
  await executeCustomerAchSearch()

  recurringPaymentNewProxy.customerCard = customerCard
  recurringPaymentNewProxy.form.dirty = addToArray(recurringPaymentNewProxy.form.dirty, VirtualTerminalProxyFormFieldNamesEnum.CustomerCard)
  resetVirtualTerminalCardAddProxy()
}
